import { NeededUserInfo } from '@components/AssigneeInput/types';
import { ICustomField } from '@models/CustomField.model';
import { IEquipmentInventory } from '@models/EquipmentInventory.model';
import { IFieldApiModel2 } from '@models/Field.model';
import { IManufacturerApiModel2 } from '@models/Manufacturer.model';
import { IProductApiModel2 } from '@models/Product.model';

import { EquipmentsMap } from '@store/machines/equipmentsPicker.schema';
import { KnownData } from '@store/redux/features/inventory/knownData';
import { mutateKnownData } from '@store/redux/features/inventory/knownData.helper';
import { pick } from '@utils/pick';
import {
  EquipmentTypeKeepNulls,
  GlobalInput,
  GlobalViewValue,
  ITagCount,
} from './types';

type ExtraFields = 'equipmentStatus' | 'tags' | 'manufacturerImg';

type PartOfEquipmentInventory = Pick<
  IEquipmentInventory,
  TSharedFields[number] | ExtraFields
>;

export interface NeededFromEq extends PartOfEquipmentInventory {
  manufacturerName?: IManufacturerApiModel2['name'] | undefined;
  user: NeededUserInfo | null | undefined;
  product: {
    id: IProductApiModel2['id'];
    model: IProductApiModel2['model'];
    imageUrl?: IProductApiModel2['imageUrl'];
  };
  fields: Pick<IFieldApiModel2, 'customFieldId' | 'content'>[];
}

export const SHARED_FIELDS = [
  'id',
  'serial',
  'purchasedDate',
  'purchasedFrom',
  'purchasedPrice',
  'installedSoftwareVersion',
  'installedFirmwareVersion',
  'licenseStartDate',
  'licenseEndDate',
  'productId',
  'userId',
  'installDate',
  'equipmentStatusId',
  'extendedWarrantyPurchased',
  'studioId',
] as const;

export const SHARED_FIELDS_GLOBAL = [
  'purchasedDate',
  'purchasedFrom',
  'purchasedPrice',
  'installedSoftwareVersion',
  'installedFirmwareVersion',
  'licenseStartDate',
  'licenseEndDate',
  'productId',
  'userId',
  'installDate',
  'equipmentStatusId',
  'extendedWarrantyPurchased',
] as const;
type TSharedFields = typeof SHARED_FIELDS;

// INIT-FORMV
// This is sending out. Don't use undefined as initial value
// Gather knownProducts (this is done in view values. Ensure you don't put stuff that changes there.)
export function getDefaultValues(
  equipments: EquipmentsMap,
  customFields: ICustomField[],
): { equips: EquipmentTypeKeepNulls[]; known: KnownData } {
  const known: KnownData = {
    knownEquipments: {},
    knownProducts: {},
    knownStatuses: {},
    knownTags: {},
    knownUsers: {},
  };
  const equips = Array.from(equipments.values(), equipment => {
    const equipmentObject = pick(equipment, SHARED_FIELDS);
    const limitedCustomFields = getCustomFieldsFresh(equipment, customFields);
    mutateKnownData(known, equipment);
    return {
      ...equipmentObject,
      tagsId: equipment.tags ? equipment.tags.map(tag => tag.id) : [],
      customFields: limitedCustomFields,
    };
  });
  return { equips, known };
}

export function getCustomFieldsFresh(
  equipment: Pick<NeededFromEq, 'fields'>,
  customFields: ICustomField[],
) {
  const entries = customFields.map(customField => {
    const fieldInEquipment = equipment.fields.find(
      field => field.customFieldId === customField.id,
    );
    const { content } = fieldInEquipment || {};
    const { id } = customField;
    const { defaultValue } = customField;
    return [
      id,
      {
        id,
        content: content ?? defaultValue ?? null,
      },
    ] as const;
  });
  return Object.fromEntries(entries);
}

export function showOnOnboard(customField: ICustomField): boolean {
  return !!customField.showOnOnboard;
}
// INIT-GLOBALFORMV
export function getDefaultGlobalValues(
  customFields: ICustomField[],
  equipments: EquipmentsMap,
): GlobalInput {
  const tagAccumulator = countTags(equipments);

  // Make no assumptions about equipment. Except existence of ids and tags.
  const equipment = {
    customFields: defaultGlobalCustomFields(customFields),
    tagsId: Object.fromEntries(tagAccumulator),
  };

  return { equipment, isDirty: false, dirtyGlobalFields: {} };
}

function countTags(equipments: EquipmentsMap) {
  const tagAccumulator = new Map<string, ITagCount>();
  equipments.forEach(equipment => {
    const { tags } = equipment;
    tags.forEach(({ id }) => {
      const tagCount = tagAccumulator.get(id);
      if (!tagCount) {
        tagAccumulator.set(id, { id, count: 1 });
        return;
      }
      tagAccumulator.set(id, { id, count: tagCount.count + 1 });
    });
  });
  return tagAccumulator;
}

function defaultGlobalCustomFields(customFields: ICustomField[]) {
  return Object.fromEntries(
    customFields.map(({ id }) => {
      return [id, { id }] as const;
    }),
  );
}

// INIT-FORMV VIEW
export function getDefaultViewValues(equipments: EquipmentsMap) {
  const views = Array.from(equipments.values(), () => {
    return {
      identificationSearchText: null,
    };
  });

  return views;
}

// INIT-FORMV GLOBAL VIEW
export function getDefaultGlobalViewValues(): GlobalViewValue {
  const view = {
    identificationSearchText: null,
  };
  return { view };
}

export function toMap(equipments: NeededFromEq[]): EquipmentsMap {
  const entries = equipments.map(
    equipment => [equipment.id, equipment] as const,
  );
  const map = new Map(entries);
  return map;
}
